import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, RefreshCcw, Trash2, ArrowLeft, Download } from "lucide-react";
import { verifyAdmin, fetchLeads, deleteLead, API } from "../lib/api";
import Navbar from "../components/Navbar";

const TOKEN_KEY = "wedelvr_admin_token";

export default function Admin() {
    const [token, setToken] = useState("");
    const [authed, setAuthed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [leads, setLeads] = useState([]);

    useEffect(() => {
        const saved = localStorage.getItem(TOKEN_KEY);
        if (saved) {
            setToken(saved);
            tryLogin(saved);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tryLogin = async (t) => {
        try {
            setLoading(true);
            await verifyAdmin(t);
            setAuthed(true);
            localStorage.setItem(TOKEN_KEY, t);
            await loadLeads(t);
        } catch {
            toast.error("Invalid admin token");
            localStorage.removeItem(TOKEN_KEY);
            setAuthed(false);
        } finally {
            setLoading(false);
        }
    };

    const loadLeads = async (t) => {
        try {
            setLoading(true);
            const data = await fetchLeads(t);
            setLeads(data.items || []);
        } catch {
            toast.error("Could not load leads");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        try {
            await deleteLead(token, id);
            setLeads((arr) => arr.filter((l) => l.id !== id));
            toast.success("Lead deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        setToken("");
        setLeads([]);
    };

    return (
        <div data-testid="admin-page" className="bg-[#0A0A0E] text-zinc-100 min-h-screen">
            <Navbar />
            <main className="pt-28 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-cyan-300 mb-3" data-testid="admin-back-link">
                            <ArrowLeft size={14} /> Back to site
                        </Link>
                        <h1 className="font-display text-3xl sm:text-4xl text-white">
                            Admin · Leads
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1 font-mono-accent">
                            EARLY ACCESS &amp; CONTACT SUBMISSIONS
                        </p>
                    </div>
                    {authed && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                onClick={async () => {
                                    try {
                                        const res = await fetch(`${API}/leads/export.csv`, {
                                            headers: { "X-Admin-Token": token },
                                        });
                                        if (!res.ok) throw new Error("Failed");
                                        // Honour server-supplied filename
                                        const cd = res.headers.get("content-disposition") || "";
                                        const m = cd.match(/filename="?([^"]+)"?/i);
                                        const filename = m?.[1] || `wedelvr-leads-${Date.now()}.csv`;
                                        const blob = await res.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = filename;
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        toast.success("CSV downloaded");
                                    } catch {
                                        toast.error("Export failed");
                                    }
                                }}
                                className="btn-secondary text-sm py-2 px-4"
                                data-testid="admin-export-btn"
                            >
                                <Download size={14} /> Export CSV
                            </button>
                            <button
                                onClick={() => loadLeads(token)}
                                className="btn-secondary text-sm py-2 px-4"
                                data-testid="admin-refresh-btn"
                            >
                                <RefreshCcw size={14} /> Refresh
                            </button>
                            <button
                                onClick={logout}
                                className="btn-secondary text-sm py-2 px-4"
                                data-testid="admin-logout-btn"
                            >
                                Logout
                            </button>
                        </div>
                    )}
                </div>

                {!authed ? (
                    <div className="card-glow rounded-2xl p-8 max-w-md" data-testid="admin-login-card">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-cyan-400/30 bg-cyan-400/10 mb-5">
                            <Lock size={18} className="text-cyan-300" />
                        </div>
                        <h2 className="font-display text-2xl text-white">Admin access</h2>
                        <p className="text-sm text-zinc-400 mt-2 mb-6">
                            Enter the admin token to view captured leads.
                        </p>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!token.trim()) return;
                                tryLogin(token.trim());
                            }}
                            className="space-y-4"
                            data-testid="admin-login-form"
                        >
                            <input
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="Admin token"
                                className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                                data-testid="admin-token-input"
                                autoFocus
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full justify-center disabled:opacity-60"
                                data-testid="admin-login-btn"
                            >
                                {loading ? "Verifying..." : "Unlock"}
                            </button>
                        </form>
                    </div>
                ) : (
                    <div data-testid="admin-leads-view">
                        <div className="card-glow rounded-2xl overflow-hidden">
                            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                                <div className="font-mono-accent text-[11px] text-zinc-500 uppercase tracking-[0.2em]">
                                    {leads.length} {leads.length === 1 ? "lead" : "leads"}
                                </div>
                                {loading && (
                                    <div className="font-mono-accent text-[11px] text-cyan-400">
                                        LOADING...
                                    </div>
                                )}
                            </div>

                            {leads.length === 0 && !loading ? (
                                <div className="p-10 text-center text-zinc-500" data-testid="admin-empty">
                                    No leads yet. Submissions from the Early Access form will appear here.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-left text-xs font-mono-accent uppercase tracking-[0.15em] text-zinc-500">
                                            <tr className="border-b border-white/5">
                                                <Th>Received</Th>
                                                <Th>Name</Th>
                                                <Th>Email</Th>
                                                <Th>Company</Th>
                                                <Th>Role</Th>
                                                <Th>Type</Th>
                                                <Th>Message</Th>
                                                <Th align="right">—</Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leads.map((l) => (
                                                <tr
                                                    key={l.id}
                                                    className="border-b border-white/5 hover:bg-white/[0.02]"
                                                    data-testid={`lead-row-${l.id}`}
                                                >
                                                    <Td>
                                                        <span className="font-mono-accent text-xs text-zinc-400">
                                                            {formatDate(l.created_at)}
                                                        </span>
                                                    </Td>
                                                    <Td className="text-white">{l.name}</Td>
                                                    <Td className="text-cyan-300">
                                                        <a href={`mailto:${l.email}`}>{l.email}</a>
                                                    </Td>
                                                    <Td>{l.company || "—"}</Td>
                                                    <Td>{l.role || "—"}</Td>
                                                    <Td>
                                                        <span className="px-2 py-1 rounded text-[10px] font-mono-accent uppercase tracking-[0.15em] border border-cyan-400/30 text-cyan-300 bg-cyan-400/5">
                                                            {l.interest}
                                                        </span>
                                                    </Td>
                                                    <Td>
                                                        <div className="max-w-xs truncate text-zinc-400" title={l.message}>
                                                            {l.message || "—"}
                                                        </div>
                                                    </Td>
                                                    <Td align="right">
                                                        <button
                                                            onClick={() => onDelete(l.id)}
                                                            className="text-zinc-500 hover:text-red-400 p-2"
                                                            aria-label="Delete lead"
                                                            data-testid={`lead-delete-${l.id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </Td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

const Th = ({ children, align = "left" }) => (
    <th className={`px-4 py-3 text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left", className = "" }) => (
    <td className={`px-4 py-3 align-top text-${align} text-zinc-300 ${className}`}>
        {children}
    </td>
);

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
